import React, { useEffect, useRef, useState } from 'react';
import {
  AddFormElements,
  Form,
  Input,
  Radio,
  RadioGroup,
  RadioParam,
  RequiredHelpPhrase,
  Row,
  SubmitButton,
  useForm,
} from '../../../_lib/Form2';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import type { AttributeGroupCreateForm, AttributeGroupEditForm } from './AttributeGroupForm.types';
import { createCollectionName } from '../../../_lib/Form2/types/CollectionParam';
import { canBeFocused, isEmptyArray } from '../../../lib/typeGuards';
import useRedirect from '../../../hooks/useRedirect';
import IUrl from '../../../types/IUrl';
import { AttributeGroupFormRuleFieldSet } from './partials/AttributeGroupFormRuleFieldSet';
import { RuleInState } from './AttributeGroupForm.types';

/**
 * Form to create and edit Attribute Groups.
 */
export function AttributeGroupForm({ form }: { form: AttributeGroupCreateForm | AttributeGroupEditForm }) {
  const maxRules = 3; // this should be defined by the backend

  const { t } = useSonTranslation('AttributeGroups');
  const [startRedirect] = useRedirect();

  const { formProps, fetchState, params } = useForm<AttributeGroupCreateForm | AttributeGroupEditForm, IUrl>({
    form,
    activateLeavingGuard: true,
  });

  // ref to check when form rules are ready
  const isInitialized = useRef<boolean>(false);

  const attributeRulesCollection = params.getByName('attributeRules');
  const title = params.findByName('title');
  const ruleCombination = params.getAllByName('ruleCombination');

  const [rules, setRules] = useState<RuleInState[]>(() =>
    attributeRulesCollection.items.map(rule => ({
      param: rule,
      generatedKey: Math.random(),
      unpersisted: false,
    })),
  );

  useEffect(() => {
    if (fetchState.type === 'success') {
      startRedirect(fetchState.response.data.url);
    }
  }, [fetchState, startRedirect]);

  // effect for handling focus by accessing the controlled elements of the form
  useEffect(() => {
    const controlledElements = formProps.ref.current?.elements;
    const lastAttributeNameSelect = createCollectionName(
      attributeRulesCollection.name,
      rules.length - 1,
      'attributeName',
    );
    const targetToFocus = controlledElements?.namedItem(lastAttributeNameSelect);

    if (targetToFocus && canBeFocused(targetToFocus) && isInitialized.current === true) {
      targetToFocus.focus();
    } else {
      isInitialized.current = true;
    }
  }, [attributeRulesCollection.name, formProps.ref, rules.length]);

  // we always should have at least one rule
  if (isEmptyArray(attributeRulesCollection.items)) {
    return null;
  }
  const firstRule = attributeRulesCollection.items[0];

  const removeRule = (generatedKey: number) => {
    setRules(prevState => prevState.filter(rule => rule.generatedKey !== generatedKey));
  };

  const addNewRule = () => {
    const newAddedRule = { param: firstRule, unpersisted: true, generatedKey: Math.random() };
    setRules(p => p.concat(newAddedRule));
  };

  // if there is no title, we assume it is the edit form
  const type = title ? 'create' : 'edit';

  return (
    <Form {...formProps} id="attribute-group-form">
      <RequiredHelpPhrase />
      {title && (
        <Row>
          <Input label={t('form.inputLabel')} param={title} />
        </Row>
      )}
      {rules.map((rule, index) => (
        <AttributeGroupFormRuleFieldSet
          key={rule.generatedKey}
          rule={rule}
          index={index}
          removeRule={removeRule}
          showRemoveButton={rules.length !== 1}
          attributeRulesCollectionName={attributeRulesCollection.name}
        />
      ))}
      {rules.length < maxRules && <AddFormElements name={t('form.addRule')} onClick={addNewRule} />}
      {rules.length > 1 && (
        <RadioGroup fieldsetModifier={['like-label', 'inline-radios']} legend={t('form.connection.label')}>
          {ruleCombination.map((radio: RadioParam<'ruleCombination', 'or' | 'and'>) => (
            <Radio key={radio?.value} label={t(`form.connection.${radio.value}`)} param={radio} />
          ))}
        </RadioGroup>
      )}
      <SubmitButton
        buttonLabel={t(`form.saveButton.${type}`)}
        messageSuccess={t(`form.status.${type}.success`)}
        messageSending={t(`form.status.${type}.sending`)}
        messageFail={t(`form.status.${type}.fail`)}
        fetchState={fetchState}
      />
    </Form>
  );
}
